import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, CheckCircle, XCircle, MessageSquare, Loader2, AlertCircle, Check, Ban, Lock } from 'lucide-react';
import api, { documentsAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';
import { useState } from 'react';
import ActivityTimeline from '../../components/ActivityTimeline';
import DocumentEditor from '../../components/documents/DocumentEditor';
import ApprovalWorkflowVisualization from '../../components/documents/ApprovalWorkflowVisualization';
import SignatureCapture from '../../components/documents/SignatureCapture';
import { isClientWorkerRole } from '../../utils/roles';
import { VisibilityBadge, VisibilityToggle } from '../../components/documents/CommentVisibility';

import BackButton from '../../components/common/BackButton';
export default function DocumentDetailPage() {
  const { id } = useParams();
  const { user, isInternalTeam } = useAuthStore();
  const queryClient = useQueryClient();
  const [revisionNotes, setRevisionNotes] = useState('');
  const [showRevisionInput, setShowRevisionInput] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentVisibility, setCommentVisibility] = useState('internal');
  const [showSignatureCapture, setShowSignatureCapture] = useState(false);
  const [showDenyInput, setShowDenyInput] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [downloadError, setDownloadError] = useState('');

  // facility_worker role cannot view documents - they only have training access
  if (isClientWorkerRole(user)) {
    return (
      <div className="space-y-6">
        <BackButton fallbackTo={"/projects"} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />
        <div className="card p-8 text-center">
          <Lock size={48} className="mx-auto mb-4 text-secondary-400" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">Access Restricted</h2>
          <p className="text-secondary-600">You do not have permission to view documents. Your access is limited to training-related content.</p>
        </div>
      </div>
    );
  }

  const { data: document, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsAPI.getById(id),
    select: (res) => res.data.document,
  });

  const { data: commentsData, refetch: refetchComments } = useQuery({
    queryKey: ['document-comments', id],
    queryFn: () => documentsAPI.getComments(id),
    select: (res) => res.data,
    enabled: !!id,
  });

  // Approve document mutation with signature
  const approveMutation = useMutation({
    mutationFn: async (signature) => {
      // First save the signature
      if (signature) {
        await documentsAPI.saveSignature(id, { signatureData: signature });
      }
      // Record the client's approval through the document workflow
      // ('approved' is not a document status; the workflow tracks client and internal approval)
      return api.post(`/drafts/client/${id}/approve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      setShowSignatureCapture(false);
    },
  });

  // Request revision mutation
  const revisionMutation = useMutation({
    mutationFn: () => api.post(`/drafts/client/${id}/request-changes`, { reason: revisionNotes.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      setRevisionNotes('');
      setShowRevisionInput(false);
    },
  });

  // Deny document mutation
  const denyMutation = useMutation({
    // The workflow has no "denied" state: declining sends the document back to Stratel with the reason recorded
    mutationFn: () => api.post(`/drafts/client/${id}/request-changes`, { reason: `Declined: ${denyReason.trim()}` }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      setDenyReason('');
      setShowDenyInput(false);
    },
  });

  const handleDownload = async (versionNumber) => {
    setDownloadError('');
    try {
      const res = await documentsAPI.getDownloadUrl(id, versionNumber);
      if (!res.data?.downloadUrl) throw new Error('No download link returned');
      window.open(res.data.downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setDownloadError(err.response?.data?.message || err.message || 'Download failed');
    }
  };

  // Add comment
  const commentMutation = useMutation({
    mutationFn: (content) => documentsAPI.addComment(id, { content, visibility: commentVisibility }),
    onSuccess: () => {
      refetchComments();
      setNewComment('');
      setCommentVisibility('internal');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500">Document not found</p>
      </div>
    );
  }

  // Check permissions based on role and service type
  // Client admins and managers can approve documents in client_review status
  const canApprove = ['facility_admin', 'facility_manager'].includes(user?.role) && document.status === 'client_review';
  const isApproving = approveMutation.isPending || revisionMutation.isPending || denyMutation.isPending;

  return (
    <div className="space-y-6">
      <BackButton fallbackTo={document.projectId ? `/projects/${document.projectId}` : '/documents'} />

      <div className="card p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-secondary-900 [overflow-wrap:anywhere]">{document.name}</h1>
            <p className="mt-1 text-secondary-600">
              Version {document.currentVersion} • {document.category}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`badge ${
              document.status === 'approved' ? 'badge-success' :
              document.status === 'denied' ? 'badge-danger' :
              document.status === 'client_review' ? 'badge-warning' :
              'badge-secondary'
            }`}>
              {document.status.replace('_', ' ')}
            </span>
          </div>
        </div>

        {document.description && (
          <p className="mt-4 text-secondary-600">{document.description}</p>
        )}

        {/* Actions */}
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            <button className="btn-primary" onClick={() => handleDownload()}>
              <Download size={18} />
              Download Current Version
            </button>
            {canApprove && (
              <>
                <button
                  onClick={() => setShowSignatureCapture(true)}
                  disabled={isApproving}
                  className="btn bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Approve this document"
                >
                  {approveMutation.isPending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <CheckCircle size={18} />
                  )}
                  Approve with Signature
                </button>
                <button
                  onClick={() => { setShowRevisionInput(!showRevisionInput); setShowDenyInput(false); }}
                  disabled={isApproving}
                  className="btn bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Request revision from the team"
                >
                  {revisionMutation.isPending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <AlertCircle size={18} />
                  )}
                  Request Revision
                </button>
                <button
                  onClick={() => { setShowDenyInput(!showDenyInput); setShowRevisionInput(false); }}
                  disabled={isApproving}
                  className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Decline this document"
                >
                  {denyMutation.isPending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <XCircle size={18} />
                  )}
                  Decline
                </button>
              </>
            )}
          </div>

          {downloadError && (
            <p className="text-sm text-red-600">{downloadError}</p>
          )}

          {/* Decline reason */}
          {showDenyInput && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-secondary-900 mb-2">
                Reason for declining
              </label>
              <p className="text-sm text-secondary-600 mb-2">
                Declining sends the document back to Stratel with your reason so they can withdraw or rework it. Use Request Revision if it only needs specific changes.
              </p>
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Explain why you are declining this document..."
                rows={3}
                className="input resize-none mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => denyMutation.mutate()}
                  disabled={!denyReason.trim() || denyMutation.isPending}
                  className="btn-danger disabled:opacity-50"
                >
                  {denyMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <XCircle size={16} />
                  )}
                  Confirm Decline
                </button>
                <button
                  onClick={() => {
                    setShowDenyInput(false);
                    setDenyReason('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Revision input */}
          {showRevisionInput && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-secondary-900 mb-2">
                Revision Notes
              </label>
              <textarea
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
                placeholder="Describe what needs to be revised..."
                rows={3}
                className="input resize-none mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => revisionMutation.mutate()}
                  disabled={!revisionNotes.trim() || revisionMutation.isPending}
                  className="btn bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  {revisionMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  Send for Revision
                </button>
                <button
                  onClick={() => {
                    setShowRevisionInput(false);
                    setRevisionNotes('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Error message */}
          {(approveMutation.isError || revisionMutation.isError || denyMutation.isError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">
                  {approveMutation.error?.message || revisionMutation.error?.message || denyMutation.error?.message}
                </p>
              </div>
            </div>
          )}

          {/* Success message */}
          {(approveMutation.isSuccess || revisionMutation.isSuccess || denyMutation.isSuccess) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
              <Check size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  {approveMutation.isSuccess && 'Document approved successfully'}
                  {revisionMutation.isSuccess && 'Revision request sent'}
                  {denyMutation.isSuccess && 'Document declined and sent back to Stratel'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Activity Timeline</h2>
        <ActivityTimeline document={document} />
      </div>
      
      {/* Version History */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-secondary-900">Version History</h2>
        </div>
        <div className="divide-y divide-secondary-200">
          {document.versions?.slice().reverse().map((version) => (
            <div key={version._id} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-secondary-900">
                  Version {version.versionNumber}
                  {version.versionNumber === document.currentVersion && (
                    <span className="ml-2 badge badge-primary">Current</span>
                  )}
                </p>
                <p className="text-sm text-secondary-500">
                  Uploaded by {version.uploadedBy?.firstName} {version.uploadedBy?.lastName} •{' '}
                  {new Date(version.uploadedAt).toLocaleDateString()}
                </p>
                {version.notes && (
                  <p className="text-sm text-secondary-600 mt-1">{version.notes}</p>
                )}
              </div>
              <button className="btn-secondary btn-sm" onClick={() => handleDownload(version.versionNumber)}>
                <Download size={16} />
                Download
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Approval Workflow Visualization */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Approval Workflow</h2>
        <ApprovalWorkflowVisualization 
          document={document}
          currentStatus={document.status}
        />
      </div>

      {/* Activity Timeline */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Activity Timeline</h2>
        <ActivityTimeline document={document} />
      </div>

      {/* Comments */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-secondary-900">Comments</h2>
          <span className="text-sm text-secondary-500">{commentsData?.count || 0} comments</span>
        </div>
        <div className="p-6">
          {commentsData?.comments?.length > 0 ? (
            <div className="space-y-4">
              {commentsData.comments.map((comment) => (
                <div key={comment._id} className="flex gap-3">
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-medium">
                      {comment.userId?.firstName?.[0]}{comment.userId?.lastName?.[0]}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-secondary-900">
                        {comment.userId?.firstName} {comment.userId?.lastName}
                      </span>
                      <span className="text-sm text-secondary-500">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                      <VisibilityBadge comment={comment} />
                      {comment.resolved && (
                        <span className="badge badge-success">Resolved</span>
                      )}
                    </div>
                    <p className="mt-1 text-secondary-600">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-secondary-500 text-center">No comments yet</p>
          )}

          {/* Add comment */}
          <div className="mt-6 pt-6 border-t border-secondary-200 space-y-2">
            <VisibilityToggle value={commentVisibility} onChange={setCommentVisibility} />
            <textarea
              placeholder={commentVisibility === 'shared' ? 'Comment Stratel will see...' : 'Internal note for your team...'}
              rows={3}
              className="input resize-none"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            {commentMutation.isError && (
              <p className="mt-2 text-sm text-red-600">
                {commentMutation.error?.response?.data?.message || 'Failed to add comment'}
              </p>
            )}
            <div className="mt-3 flex justify-end">
              <button
                className="btn-primary disabled:opacity-50"
                onClick={() => commentMutation.mutate(newComment.trim())}
                disabled={!newComment.trim() || commentMutation.isPending}
              >
                {commentMutation.isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <MessageSquare size={18} />
                )}
                Add Comment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Signature Capture Modal */}
      {showSignatureCapture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">Digital Signature</h3>
              <p className="text-sm text-secondary-600 mt-1">Sign below to approve this document</p>
            </div>
            <div className="p-6">
              <SignatureCapture
                onSave={(signatureData) => {
                  approveMutation.mutate(signatureData);
                }}
                onCancel={() => setShowSignatureCapture(false)}
                isLoading={approveMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
